import { DataTable } from "../../components/DataTable.tsx";
import { Button } from "../../components/ui/button.tsx";
import { FaEye, FaPencilAlt } from "react-icons/fa";
import Modal from "../../components/Modal.tsx";
import { Label } from "../../components/ui/label.tsx";
import React, { useEffect, useState } from "react";
import {
  requestService,
  transactionService,
  transactionDataMap,
} from "../../services";
import useUserStore2 from "../../stores/userStore2";
import ShortenedText from "../../components/ShortenedText";
import { createItemStore } from "../../stores/createItemStore";
import LoadingImage from "../../components/LoadingImage";
import { toast } from "react-toastify";
import StatusBadge from "../../components/table/StatusBadge"

const columns = [
  {
    accessorKey: "dateCreated",
    header: "Date Created",
    cell: ({ row }) => (
      <span>
        {new Date(row.getValue("dateCreated")).toLocaleString().split(",")[0]}
      </span>
    ),
  },

  {
    accessorKey: "pocName",
    header: "POC Name",
    cell: ({ row }) => <span>{row.getValue("pocName")}</span>,
  },
  {
    accessorKey: "pocaName",
    header: "POCA Name",
    cell: ({ row }) => <span>{row.getValue("pocaName")}</span>,
  },
  {
    accessorKey: "materialCategory",
    header: "Material Category",
    cell: ({ row }) => <span>{row.getValue("materialCategory")}</span>,
  },
  {
    accessorKey: "quantity",
    header: "Quantity",
    cell: ({ row }) => (
      <span>
        {row.getValue("quantity")} {row.original.materialUnit}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status");
      return (
        <StatusBadge status={status} />
      );
    },
  },
];

// ========================================================
// Step 1: Zustand store instantiation
// ========================================================
const storeName = "my-transactions";
const useItems = createItemStore(
  storeName,
  (transaction) => transaction.transactionId,
  (args) => transactionService.getTransactions(args[0], args[1]), // Modified to use args
  transactionDataMap
);
// ========================================================

export default function Transaction() {
  const [isOpen, setIsOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [description, setDescription] = useState(null);
  const [reload, setReload] = useState(false);

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setIsViewOpen(true);
  };

  const handleEditTransaction = (transaction) => {
    setSelectedTransaction(transaction); // Store the selected transaction data
    setIsOpen(true); // Open the edit modal
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedTransaction) return;

    const { user } = useUserStore2.getState(); // Get user data from useUserStore2 instead
    const currentProfile = user.currentProfile;
    const requestData = {
      RequestEntityId: user.id, // Logged-in user ID from useUserStore2
      RequestProfileId: currentProfile.id, // Note: useUserStore2 uses id instead of profileId
      CommunityId: currentProfile.communityId, // Get from currentProfile
      GroupId: currentProfile.groupId, // Get from groupId
      TransactionId: selectedTransaction?.transactionId || null, // Taken from selected row
      Type: "T", // 'Invitation', 'Suspension', 'Removal', 'Tx Review' so is I S R T
      Description: description, // From modal input
      Comment: "", // Empty for now
    };

    try {
      setIsSubmitting(true);
      await requestService.addRequest(requestData);
      console.log("Request submitted successfully!", requestData);
      toast.success("🎉 Request Submitted Successfully!", {
        autoClose: 2000,
      });
      setIsOpen(false); // Close modal on success
    } catch (error) {
      toast.error("❌ Failed to Submit Request", {
        autoClose: 2000,
      });
      console.error("Failed to submit request:", error);
    } finally {
      setReload(!reload);
      setIsSubmitting(false);
    }
  };

  const actions = (row) => [
    {
      label: "View details",
      icon: <FaEye />,
      onClick: () => handleViewTransaction(row),
      variant: "dropPlain",
    },
    {
      label: "Request Review",
      icon: <FaPencilAlt />,
      onClick: () => handleEditTransaction(row),
    },
  ];

  // Replace useUserStore with useUserStore2
  const { user } = useUserStore2((state) => state);
  // console.log(`user:`, user);
  // console.log(`userId:`, user?.id); // Use user.id instead of userId

  // ========================================================
  // Step 2: Zustand store and page hydration
  // ========================================================
  const { isLoading, fetchItems, getSortedFiltered } = useItems(
    (state) => state
  );

  const data = user?.currentProfile?.id
    ? getSortedFiltered(
        (transaction) => transaction.profId === user?.currentProfile?.id
      )
    : getSortedFiltered();
  const showLoading = data.length === 0 && isLoading;
  // console.log(`${storeName}:`, data);
  // console.log(`user?.currentProfile?.id:`, user?.currentProfile?.id);

  const arg = [user?.id, user?.currentProfile?.id];
  // console.log(`arg:`, arg);

  useEffect(() => {
    if (user?.id && user?.currentProfile?.id) {
      fetchItems({}, arg);
    }
  }, [user?.id, user?.currentProfile?.id, reload]);
  // ========================================================

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        <div className="p-8 flex flex-col">
          <div className="bg-white shadow rounded-lg p-4 sm:p-6 text-sm md:mt-0">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
              <div className="text-base sm:text-lg font-semibold text-[#111827] mb-4 sm:mb-0">
                My Transaction List
              </div>
            </div>
            <hr />
            <div className="overflow-x-auto w-full max-w-screen-lg">
              {showLoading ? (
                <LoadingImage />
              ) : data.length > 0 ? (
                <DataTable
                  columns={columns}
                  data={data.reverse()}
                  actions={actions}
                />
              ) : (
                <div className="text-center text-gray-500 py-6">
                  No requests found.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submit transaction review */}
        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title="Review Request"
          actions={
            <div className="flex justify-between w-full">
              <Button variant="secondary" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          }
        >
          {/* Modal Content */}
          <div className="max-h-[70vh] overflow-y-auto space-y-6">
            {/* Static Information */}
            <div className="space-y-4">
              <div className="bg-teal-50 grid grid-cols-3 gap-4 p-2">
                <div>
                  <Label className="text-gray-500 text-xs">
                    Transaction ID
                  </Label>
                  <span className="text-sm font-semibold">
                    <ShortenedText text={selectedTransaction?.transactionId} />
                  </span>
                </div>
                <div>
                  <Label className="text-gray-700 text-xs">Date Created</Label>
                  <span className="text-sm font-semibold">
                    {selectedTransaction?.dateCreated}
                  </span>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs block">Status</Label>
                  <span className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold inline-block">
                    {selectedTransaction?.status}
                  </span>
                </div>
              </div>

              <div className="border-t border-gray-300"></div>

              {/* Alternating Background Rows with Left-Aligned Values */}
              <div className="text-left space-y-2">
                {[
                  {
                    label: "POC ID",
                    value: selectedTransaction?.pocId,
                    bg: "bg-gray-100",
                  },
                  {
                    label: "POC Name",
                    value: selectedTransaction?.pocName,
                    bg: "bg-white",
                  },
                  {
                    label: "Material Category",
                    value: selectedTransaction?.materialCategory,

                    bg: "bg-gray-100",
                  },
                  {
                    label: "Quantity",
                    value: selectedTransaction?.materialUnit
                      ? (selectedTransaction?.quantity + " " + selectedTransaction?.materialUnit)
                      : selectedTransaction?.quantity,
                    bg: "bg-white",
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className={`grid grid-cols-2 p-2 ${item.bg}`}
                  >
                    <Label className="text-gray-500">{item.label}:</Label>
                    <span className="text-gray-700">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-300"></div>

            {/* Editable Field */}
            <div>
              <Label className="text-gray-500">Description:</Label>
              <div className="relative">
                <textarea
                  className="w-full border border-gray-300 p-2 rounded-lg"
                  rows={4}
                  maxLength={50}
                  placeholder="Enter Description..."
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
          </div>
        </Modal>

        {/* View Modal */}
        <Modal
          isOpen={isViewOpen}
          onClose={() => setIsViewOpen(false)}
          title="View My Transaction Details"
          description="Here are the details for this transaction."
          icon={<FaEye className="text-blue-500" />}
          actions={
            <div className="flex justify-between w-full">
              <Button
                variant={"secondary"}
                onClick={() => setIsViewOpen(false)}
              >
                Close
              </Button>
            </div>
          }
        >
          {/* Modal Content Wrapper */}
          <div className="max-h-[70vh] overflow-y-auto space-y-6 px-4">
            {selectedTransaction ? (
              <div className="grid gap-4">
                <div className="flex items-center px-4 py-2 bg-gray-100">
                  <div className="font-semibold w-1/2 text-left">
                    Transaction ID:
                  </div>
                  <span className="text-left w-1/2">
                    {selectedTransaction.transactionId}
                  </span>
                </div>

                <div className="flex items-center px-4 py-2 bg-white">
                  <div className="font-semibold w-1/2 text-left">
                    Date Created:
                  </div>
                  <span className="text-left w-1/2">
                    {selectedTransaction.dateCreated || "N/A"}
                  </span>
                </div>

                <div className="flex items-center px-4 py-2 bg-gray-100">
                  <div className="font-semibold w-1/2 text-left">
                    Date Updated:
                  </div>
                  <span className="text-left w-1/2">
                    {selectedTransaction.dateUpdated || "N/A"}
                  </span>
                </div>

                <div className="flex items-center px-4 py-2 bg-white">
                  <div className="font-semibold w-1/2 text-left">
                    Profile ID:
                  </div>
                  <span className="text-left w-1/2">
                    {selectedTransaction.profId || "N/A"}
                  </span>
                </div>

                <div className="flex items-center px-4 py-2 bg-gray-100">
                  <div className="font-semibold w-1/2 text-left">
                    Profile Name:
                  </div>
                  <span className="text-left w-1/2">
                    {selectedTransaction.profileName || "N/A"}
                  </span>
                </div>

                <div className="flex items-center px-4 py-2 bg-white">
                  <div className="font-semibold w-1/2 text-left">POC ID:</div>
                  <span className="text-left w-1/2">
                    {selectedTransaction.pocId || "N/A"}
                  </span>
                </div>

                <div className="flex items-center px-4 py-2 bg-gray-100">
                  <div className="font-semibold w-1/2 text-left">POC Name:</div>
                  <span className="text-left w-1/2">
                    {selectedTransaction.pocName || "N/A"}
                  </span>
                </div>

                <div className="flex items-center px-4 py-2 bg-white">
                  <div className="font-semibold w-1/2 text-left">
                    Material Category:
                  </div>
                  <span className="text-left w-1/2">
                    {selectedTransaction.materialCategory || "N/A"}
                  </span>
                </div>

                <div className="flex items-center px-4 py-2 bg-gray-100">
                  <div className="font-semibold w-1/2 text-left">Quantity:</div>
                  <span className="text-left w-1/2">
                    {selectedTransaction.quantity || "-"}{" "}
                    {selectedTransaction.materialUnit}
                  </span>
                </div>

                <div className="flex items-center px-4 py-2 bg-white">
                  <div className="font-semibold w-1/2 text-left">Status:</div>
                  <span className="text-left w-1/2">
                    {selectedTransaction.status || "N/A"}
                  </span>
                </div>
              </div>
            ) : (
              <p>No data available</p>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
}
